import { GraphicsQuality } from "./types";

export interface GraphicsSettings {
  quality: GraphicsQuality;
  shadowsEnabled: boolean;
  shadowMapSize: number;
  grassDensity: number;
  grassDistance: number;
  cloudQuality: number;
  fogEnabled: boolean;
  fogDensity: number;
  grassWidthMultiplier: number;
  grassHeightMultiplier: number;
}

export const GRAPHICS_PRESETS: Record<GraphicsQuality, GraphicsSettings> = {
  low: {
    quality: "low",
    shadowsEnabled: false,
    shadowMapSize: 512,
    grassDensity: 0.0,
    grassDistance: 30,
    cloudQuality: 0.1,
    fogEnabled: true,
    fogDensity: 0.5,
    grassWidthMultiplier: 0.0,
    grassHeightMultiplier: 0.0,
  },
  medium: {
    quality: "medium",
    shadowsEnabled: true,
    shadowMapSize: 1024,
    grassDensity: 0.5,
    grassDistance: 60,
    cloudQuality: 0.5,
    fogEnabled: true,
    fogDensity: 0.7,
    grassWidthMultiplier: 0.9,
    grassHeightMultiplier: 0.9,
  },
  high: {
    quality: "high",
    shadowsEnabled: true,
    shadowMapSize: 2048,
    grassDensity: 1.0,
    grassDistance: 90,
    cloudQuality: 1.0,
    fogEnabled: true,
    fogDensity: 1.0,
    grassWidthMultiplier: 1.0,
    grassHeightMultiplier: 1.0,
  },
};

export class GraphicsSettingsManager {
  private static instance: GraphicsSettingsManager;
  private currentSettings: GraphicsSettings;
  private lastAppliedSettings: GraphicsSettings | null = null;
  private fpsHistory: number[] = [];
  private readonly FPS_SAMPLE_SIZE = 60;
  private fpsRafId: number | null = null;

  private constructor() {
    this.currentSettings = { ...GRAPHICS_PRESETS.medium };
  }

  public static getInstance(): GraphicsSettingsManager {
    if (!GraphicsSettingsManager.instance) {
      GraphicsSettingsManager.instance = new GraphicsSettingsManager();
    }
    return GraphicsSettingsManager.instance;
  }

  public getCurrentSettings(): GraphicsSettings {
    return { ...this.currentSettings };
  }

  public applyPreset(quality: GraphicsQuality): void {
    this.currentSettings = { ...GRAPHICS_PRESETS[quality] };
    this.applySettings();
  }

  private settingsEqual(settings1: GraphicsSettings, settings2: GraphicsSettings): boolean {
    return JSON.stringify(settings1) === JSON.stringify(settings2);
  }

  private applySettings(forceApply: boolean = false): void {
    // Skip if settings are the same as last applied and forceApply is false
    if (this.lastAppliedSettings && this.settingsEqual(this.currentSettings, this.lastAppliedSettings) && !forceApply) {
      return;
    }

    console.log("[GraphicsManager] Current settings before apply:", this.currentSettings);
    console.log("[GraphicsManager] DOM State:", {
      documentReady: document.readyState,
      canvasExists: !!document.querySelector("canvas"),
      timestamp: performance.now(),
    });

    // Emit a single event that all components can listen to
    const event = new CustomEvent("graphics:settingsChanged", {
      detail: { settings: this.currentSettings },
      bubbles: true, // Allow event to bubble up through the DOM
      composed: true, // Allow event to cross shadow DOM boundaries
    });

    // Choose the best element to dispatch the event from
    // Use canvas if available, otherwise fallback to document
    const rendererElement = document.querySelector("canvas");
    const targetElement = rendererElement || document;

    console.log("[GraphicsManager] Creating event with settings:", event.detail);

    console.log(`[GraphicsManager] Dispatching event from ${rendererElement ? "canvas" : "document"}`);

    // Dispatch only ONCE
    targetElement.dispatchEvent(event);

    console.log("[GraphicsManager] Event dispatched at:", performance.now());

    // Save the applied settings to avoid reapplying the same settings
    this.lastAppliedSettings = { ...this.currentSettings };
    console.log("[GraphicsManager] Settings applied and event dispatched");
  }

  // Add method to allow forced reapplication of settings
  public forceApplySettings(): void {
    this.applySettings(true);
  }

  public startAutoDetection(): void {
    this.stopAutoDetection(); // Clear any existing interval
    this.fpsHistory = [];

    // Start with medium settings
    this.applyPreset("medium");

    let lastFrameTime = performance.now();

    const measureFrame = () => {
      const now = performance.now();
      const delta = now - lastFrameTime;
      lastFrameTime = now;

      // Ignore absurdly large gaps (tab inactive, throttled timers, etc.)
      if (delta > 0 && delta < 1000) {
        const fps = 1000 / delta;
        this.fpsHistory.push(fps);
        if (this.fpsHistory.length > this.FPS_SAMPLE_SIZE) {
          this.fpsHistory.shift();
        }

        if (this.fpsHistory.length === this.FPS_SAMPLE_SIZE) {
          this.adjustSettingsBasedOnPerformance();
        }
      }

      this.fpsRafId = window.requestAnimationFrame(measureFrame);
    };

    this.fpsRafId = window.requestAnimationFrame(measureFrame);
  }

  public stopAutoDetection(): void {
    if (this.fpsRafId !== null) {
      window.cancelAnimationFrame(this.fpsRafId);
      this.fpsRafId = null;
    }
  }

  private adjustSettingsBasedOnPerformance(): void {
    const avgFps = this.fpsHistory.reduce((a, b) => a + b) / this.fpsHistory.length;

    // Store the current quality setting
    const currentQuality = this.currentSettings.quality;
    let targetQuality = currentQuality;

    // Determine target quality based on performance
    if (avgFps < 30 && currentQuality !== "low") {
      targetQuality = "low";
    } else if (avgFps > 58 && currentQuality !== "high") {
      targetQuality = "high";
    } else if (avgFps < 45 && currentQuality === "high") {
      targetQuality = "medium";
    }

    // Only apply preset if the quality needs to change
    if (targetQuality !== currentQuality) {
      console.log(
        `[GraphicsManager] Auto-adjusting quality from ${currentQuality} to ${targetQuality} based on FPS: ${avgFps.toFixed(
          2
        )}`
      );
      this.applyPreset(targetQuality as GraphicsQuality);
    }
  }
}
